import { Card, CardBody, Col, Row, Spinner, Table, Badge } from "reactstrap"
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts"

import PageWrapper from "components/Common/PageWrapper"
import { useAccountDashboard } from "hooks/useAccountDashboard"
import { formatCurrency } from "utils/formatCurrency"

const StatCard = ({ title, value, sub, color, icon }) => (
  <Card className="border-0 shadow-sm h-100">
    <CardBody className="d-flex align-items-center gap-3 p-3">
      <div
        className={`avatar-sm rounded d-flex align-items-center justify-content-center bg-${color} bg-opacity-10 flex-shrink-0`}
      >
        <i className={`${icon} text-${color} font-size-20`} />
      </div>
      <div className="overflow-hidden">
        <p className="text-muted mb-1 small fw-semibold text-truncate">{title}</p>
        <h5 className="mb-0 fw-bold">{value}</h5>
        {sub && <p className="text-muted mb-0 small">{sub}</p>}
      </div>
    </CardBody>
  </Card>
)

const AccountDashboard = () => {
  document.title = "Account Dashboard"

  const { data: response, isLoading, refetch } = useAccountDashboard()
  const dash = response?.data

  if (isLoading) {
    return (
      <PageWrapper title="Account Dashboard" breadcrumbItem="Dashboard">
        <div className="text-center py-5">
          <Spinner color="primary" />
        </div>
      </PageWrapper>
    )
  }

  const totalBalance = dash?.total_balance ?? 0
  const monthlyIncome = dash?.monthly_income ?? 0
  const monthlyExpenses = dash?.monthly_expenses ?? 0
  const outstandingLoans = dash?.outstanding_loans ?? { count: 0, total: 0 }
  const accountBalances = dash?.account_balances ?? []
  const monthlyTrend = dash?.monthly_trend ?? []

  return (
    <PageWrapper title="Account Dashboard" breadcrumbItem="Dashboard">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">Financial Overview</h5>
        <button className="btn btn-sm btn-outline-secondary" onClick={() => refetch()}>
          <i className="mdi mdi-refresh me-1" />
          Refresh
        </button>
      </div>

      {/* Stat Cards */}
      <Row className="g-3 mb-4">
        <Col xs={12} sm={6} xl={3}>
          <StatCard
            title="Total Account Balance"
            value={formatCurrency(totalBalance, "BDT")}
            color="primary"
            icon="mdi mdi-bank-outline"
          />
        </Col>
        <Col xs={12} sm={6} xl={3}>
          <StatCard
            title="Income This Month"
            value={formatCurrency(monthlyIncome, "BDT")}
            color="success"
            icon="mdi mdi-trending-up"
          />
        </Col>
        <Col xs={12} sm={6} xl={3}>
          <StatCard
            title="Expenses This Month"
            value={formatCurrency(monthlyExpenses, "BDT")}
            color="danger"
            icon="mdi mdi-trending-down"
          />
        </Col>
        <Col xs={12} sm={6} xl={3}>
          <StatCard
            title="Outstanding Loans"
            value={formatCurrency(outstandingLoans.total, "BDT")}
            sub={`${outstandingLoans.count} active loan${outstandingLoans.count !== 1 ? "s" : ""}`}
            color="warning"
            icon="mdi mdi-cash-clock"
          />
        </Col>
      </Row>

      {/* Chart + Account Balances */}
      <Row className="g-3">
        <Col xs={12} lg={8}>
          <Card className="border-0 shadow-sm h-100">
            <CardBody>
              <h6 className="fw-semibold mb-3">Monthly Income vs Expenses (Last 6 Months)</h6>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={monthlyTrend} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} tickFormatter={v => formatCurrency(v, "BDT")} width={90} />
                  <Tooltip formatter={(value) => formatCurrency(value, "BDT")} />
                  <Legend />
                  <Bar dataKey="income" name="Income" fill="#34c38f" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="expenses" name="Expenses" fill="#f46a6a" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardBody>
          </Card>
        </Col>

        <Col xs={12} lg={4}>
          <Card className="border-0 shadow-sm h-100">
            <CardBody>
              <h6 className="fw-semibold mb-3">Account Balances</h6>
              {accountBalances.length === 0 ? (
                <p className="text-muted small">No accounts found.</p>
              ) : (
                <div className="table-responsive">
                  <Table size="sm" className="mb-0">
                    <thead>
                      <tr>
                        <th className="border-0 text-muted fw-semibold small">Account</th>
                        <th className="border-0 text-muted fw-semibold small text-end">Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {accountBalances.map(acc => (
                        <tr key={acc.id}>
                          <td className="border-0 py-2">
                            <div className="fw-semibold small">{acc.name}</div>
                            {acc.account_type && (
                              <div className="text-muted" style={{ fontSize: "0.7rem" }}>{acc.account_type}</div>
                            )}
                          </td>
                          <td className="border-0 py-2 text-end">
                            <Badge
                              color={acc.balance < 0 ? "danger" : "success"}
                              className="font-monospace"
                            >
                              {formatCurrency(acc.balance, acc.currency || "BDT")}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </PageWrapper>
  )
}

export default AccountDashboard
